/**
 * Tawk.to live chat loader.
 *
 * Visitor identity (name/email) must be set on Tawk_API.visitor BEFORE the
 * script downloads, otherwise the widget treats the visitor as anonymous.
 * Custom attributes can be pushed later via setAttributes once the SDK has
 * loaded (Tawk_API.onLoad fires once when the widget is ready).
 *
 * @see https://developer.tawk.to/jsapi/
 */

export interface TawkVisitor {
  name?: string;
  email?: string;
}

export interface TawkApi {
  visitor?: TawkVisitor;
  onLoad?: () => void;
  showWidget?: () => void;
  hideWidget?: () => void;
  setAttributes?: (
    attributes: Record<string, string>,
    callback?: (error?: unknown) => void
  ) => void;
  [key: string]: unknown;
}

declare global {
  interface Window {
    Tawk_API?: TawkApi;
    Tawk_LoadStart?: Date;
  }
}

const TAWK_SCRIPT_URL =
  'https://embed.tawk.to/6a0b39c83507531c343e00f5/1jottjdjk';

function isLoaded(): boolean {
  return typeof window.Tawk_API?.showWidget === 'function';
}

function whenReady(callback: () => void): void {
  const api = (window.Tawk_API = window.Tawk_API || {});
  if (isLoaded()) {
    callback();
    return;
  }
  const previous = api.onLoad;
  api.onLoad = () => {
    previous?.();
    callback();
  };
}

export function loadTawk(visitor?: TawkVisitor): void {
  if (document.querySelector(`script[src="${TAWK_SCRIPT_URL}"]`)) {
    return;
  }

  const api = (window.Tawk_API = window.Tawk_API || {});
  if (visitor) {
    api.visitor = visitor;
  }
  window.Tawk_LoadStart = new Date();

  const script = document.createElement('script');
  script.async = true;
  script.src = TAWK_SCRIPT_URL;
  script.charset = 'UTF-8';
  script.setAttribute('crossorigin', '*');

  const firstScript = document.getElementsByTagName('script')[0];
  firstScript.parentNode?.insertBefore(script, firstScript);
}

export function showTawk(): void {
  whenReady(() => window.Tawk_API?.showWidget?.());
}

export function hideTawk(): void {
  whenReady(() => window.Tawk_API?.hideWidget?.());
}

export function setTawkAttributes(attributes: Record<string, string>): void {
  whenReady(() => window.Tawk_API?.setAttributes?.(attributes));
}
